import React from "react"
import { Navigate, Route, Routes, useLocation } from "react-router-dom"
import { useAuth } from "@clerk/react"
import {
  isAuthenticationRoute,
  isPublicShareRoute,
  isSuperAdminRoute,
} from "./providers/OrganizationAccessProvider"
import useLoggedInUser from "../hooks/use-logged-in-user"
import { useUserPermissions } from "../hooks/use-user-permissions"
import { useCanManageEnterpriseSso } from "../hooks/use-can-manage-enterprise-sso"

import Sidebar from "./navbar/Sidebar"
import AccessState from "./auth/AccessState"
import FormStructureOverview from "./temp/FormStructureOverview"
import { isLearnWorldsRoute } from "../lib/learnworlds"

const LearnWorldsPage = React.lazy(() => import("../pages/auth/LearnWorlds"))

// Separate component boundary: do not run Vivid permissions or admission hooks for Grow-only members.
const Router = () => {
  const location = useLocation()
  const { userId, orgId } = useAuth()
  if (isLearnWorldsRoute(location.pathname)) {
    return (
      <React.Suspense fallback={<LoadingScreen />}>
        <LearnWorldsPage key={`${userId}:${orgId}:${location.pathname}${location.search}`} />
      </React.Suspense>
    )
  }
  return <VividRouter />
}

const AdminRoute = ({ children }: { children: React.ReactNode }) => {
  const user = useLoggedInUser()
  const { hasPermission } = useUserPermissions()
  const canManageEnterpriseSso = useCanManageEnterpriseSso()

  const canAccessAdmin = user?.isAdmin || hasPermission("admin.access") || canManageEnterpriseSso

  if (!canAccessAdmin) {
    return <Navigate to="/dashboard/summary" replace />
  }

  return <>{children}</>
}

const AdminPageGuard = ({
  permission,
  children,
}: {
  permission: string
  children: React.ReactNode
}) => {
  const { hasPermission } = useUserPermissions()

  if (!hasPermission(permission)) {
    return <Navigate to="/dashboard/summary" replace />
  }

  return <>{children}</>
}

const EnterpriseSsoGuard = ({ children }: { children: React.ReactNode }) => {
  const canManageEnterpriseSso = useCanManageEnterpriseSso()

  if (!canManageEnterpriseSso) {
    return <Navigate to="/dashboard/summary" replace />
  }

  return <>{children}</>
}

const PermissionGuard = ({
  permission,
  children,
}: {
  permission: string
  children: React.ReactNode
}) => {
  const { hasPermission } = useUserPermissions()

  if (!hasPermission(permission)) {
    return <Navigate to="/dashboard/summary" replace />
  }

  return <>{children}</>
}

const SignInPage = React.lazy(() => import("../pages/auth/SignIn"))
const SignUpPage = React.lazy(() => import("../pages/auth/SignUp"))
const AccessDeniedPage = React.lazy(() => import("../pages/auth/AccessDenied"))

// dashboard
const Lagging = React.lazy(() => import("./dashboard/lagging/Lagging"))
const Leading = React.lazy(() => import("./dashboard/leading/Leading"))
const DashboardConfiguration = React.lazy(() => import("./dashboard/configuration/Configuration"))

// groups
const CreateGroup = React.lazy(() => import("./groups/create-group/CreateGroup"))
const ViewGroup = React.lazy(() => import("./groups/view-group/ViewGroup"))

// admin
const OrganizationManagement = React.lazy(
  () => import("./admin/organization-management/OrganizationManagement"),
)
const HoursEntry = React.lazy(() => import("./admin/organization-management/HoursEntry"))
const UserManagement = React.lazy(() => import("../pages/admin/UserManagement"))
const UserEntityAssignments = React.lazy(() => import("../pages/admin/UserEntityAssignments"))
const IdentityAndSso = React.lazy(() => import("./admin/identity-and-sso/IdentityAndSso"))

// incidents
const Incidents = React.lazy(() => import("./incidents/reports/Reports"))
const Incident = React.lazy(() => import("./incidents/reports/id/Report"))
const IncidentForms = React.lazy(() => import("./incidents/forms/Forms"))

// inspections
const InspectionReports = React.lazy(() => import("./inspections/reports/Reports"))
const InspectionReport = React.lazy(() => import("./inspections/reports/id/Report"))
const InspectionForms = React.lazy(() => import("./inspections/forms/Forms"))
const InspectionForm = React.lazy(() => import("./inspections/forms/id/Form"))
const InspectionFormBuilder = React.lazy(() => import("./inspections/forms/id/FormBuilder"))

// issues
const Forms = React.lazy(() => import("./issues/forms/Forms"))
const Form = React.lazy(() => import("./issues/forms/id/Form"))
const IssueFormBuilder = React.lazy(() => import("./issues/forms/id/FormBuilder"))

const IssueReport = React.lazy(() => import("./issues/reports/id/Report"))
const IssueReports = React.lazy(() => import("./issues/reports/Reports"))
const CreateReport = React.lazy(() => import("./reports/create/CreateReport"))

// settings
const Profile = React.lazy(() => import("./settings/Profile"))
const Support = React.lazy(() => import("./settings/Support"))
const FAQ = React.lazy(() => import("./settings/FAQ"))

// temp vizualizations for org hierarchy
const DatasetOverlapViz = React.lazy(() => import("./temp/DatasetOverlapViz"))
const PublicFormStructureOverview = React.lazy(() => import("./temp/PublicFormStructureOverview"))
const PublicIssueForm = React.lazy(() => import("./public/PublicIssueForm"))

// status
const Status = React.lazy(() => import("./status/status"))

// super admin
const SuperAdminLayout = React.lazy(() => import("../pages/super-admin/SuperAdminLayout"))
const SuperAdminDashboard = React.lazy(() => import("../pages/super-admin/SuperAdminDashboard"))
const ClerkOrganizations = React.lazy(() => import("../pages/super-admin/ClerkOrganizations"))
const ClerkOrganizationDetail = React.lazy(
  () => import("../pages/super-admin/ClerkOrganizationDetail"),
)
const VividOrganizations = React.lazy(() => import("../pages/super-admin/VividOrganizations"))
const VividOrganizationDetail = React.lazy(
  () => import("../pages/super-admin/VividOrganizationDetail"),
)
const CreateVividOrganization = React.lazy(
  () => import("../pages/super-admin/CreateVividOrganization"),
)
const RefinedOrganizations = React.lazy(() => import("../pages/super-admin/RefinedOrganizations"))
const CreateRefinedOrganization = React.lazy(
  () => import("../pages/super-admin/CreateRefinedOrganization"),
)
const AskTenants = React.lazy(() => import("../pages/super-admin/AskTenants"))
const CreateAskTenant = React.lazy(() => import("../pages/super-admin/CreateAskTenant"))
const SystemStatus = React.lazy(() => import("../pages/super-admin/SystemStatus"))
const DataIntegrityMonitor = React.lazy(() => import("../pages/super-admin/DataIntegrityMonitor"))
const UserVisibilityDiagnostics = React.lazy(
  () => import("../pages/super-admin/UserVisibilityDiagnostics"),
)

const LoadingScreen = () => (
  <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-[#5D82ED] to-[#C08AF8]">
    <div className="text-center">
      <div className="mb-6 flex justify-center">
        <div className="rounded-lg bg-white p-3 shadow-lg">
          <img src="/plenum-logo.svg" alt="Plenum Logo" className="h-12 w-12" />
        </div>
      </div>
      <div className="flex items-center justify-center gap-2">
        <div className="h-2 w-2 animate-bounce rounded-full bg-white [animation-delay:-0.3s]" />
        <div className="h-2 w-2 animate-bounce rounded-full bg-white [animation-delay:-0.15s]" />
        <div className="h-2 w-2 animate-bounce rounded-full bg-white" />
      </div>
    </div>
  </div>
)

const VividRouter = () => {
  const { isLoaded, isSignedIn } = useAuth()
  const location = useLocation()
  const user = useLoggedInUser()
  const {
    accessDenied,
    accessFailure,
    retryAccess,
    isVerifyingAccess,
    hasRole,
    isLoading: permissionsLoading,
  } = useUserPermissions()

  // Show loading while Clerk is initializing
  if (!isLoaded) {
    return <LoadingScreen />
  }

  // Public share route for incident form structure visualization
  if (isPublicShareRoute(location.pathname)) {
    return (
      <React.Suspense fallback={<LoadingScreen />}>
        <Routes>
          <Route
            path="/public/form-structure-overview/:shareId"
            Component={PublicFormStructureOverview}
          />
          <Route path="/public/form-structure-overview" Component={PublicFormStructureOverview} />
          <Route
            path="/public/issues/:organizationId/:formId/:linkId"
            Component={PublicIssueForm}
          />
          <Route path="*" element={<Navigate to="/sign-in" replace />} />
        </Routes>
      </React.Suspense>
    )
  }

  // Not signed in at all → only auth/status routes
  if (!isSignedIn) {
    return (
      <React.Suspense fallback={<LoadingScreen />}>
        <Routes>
          <Route path="/sign-in/*" Component={SignInPage} />
          <Route path="/sign-up/*" Component={SignUpPage} />
          <Route path="/status" Component={Status} />
          <Route path="*" element={<Navigate to="/sign-in" replace />} />
        </Routes>
      </React.Suspense>
    )
  }

  // From here on: Clerk says we're signed in

  if (location.pathname === "/status") {
    return (
      <React.Suspense fallback={<LoadingScreen />}>
        <Status />
      </React.Suspense>
    )
  }

  if (isAuthenticationRoute(location.pathname)) {
    return <Navigate to="/dashboard/summary" replace />
  }

  // Super admin routes - accessible when signed in, regardless of Vivid access
  // The SuperAdminLayout handles its own access check
  if (isSuperAdminRoute(location.pathname)) {
    return (
      <React.Suspense fallback={<LoadingScreen />}>
        <Routes>
          <Route path="/super-admin" element={<SuperAdminLayout />}>
            <Route index element={<SuperAdminDashboard />} />
            <Route path="clerk-organizations" element={<ClerkOrganizations />} />
            <Route path="clerk-organizations/:clerkOrgId" element={<ClerkOrganizationDetail />} />
            <Route path="vivid-organizations" element={<VividOrganizations />} />
            <Route path="vivid-organizations/create" element={<CreateVividOrganization />} />
            <Route path="vivid-organizations/:vividOrgId" element={<VividOrganizationDetail />} />
            <Route path="refined-organizations" element={<RefinedOrganizations />} />
            <Route path="refined-organizations/create" element={<CreateRefinedOrganization />} />
            <Route path="ask-tenants" element={<AskTenants />} />
            <Route path="ask-tenants/create" element={<CreateAskTenant />} />
            <Route path="system-status" element={<SystemStatus />} />
            <Route path="data-integrity" element={<DataIntegrityMonitor />} />
            <Route path="visibility-diagnostics" element={<UserVisibilityDiagnostics />} />
          </Route>
        </Routes>
      </React.Suspense>
    )
  }

  if (accessFailure && accessFailure.kind !== "denied")
    return <AccessState failure={accessFailure} retry={retryAccess} pending={isVerifyingAccess} />

  // User is signed in but doesn't have access to Vivid
  if (accessDenied) {
    return (
      <React.Suspense fallback={<LoadingScreen />}>
        <AccessDeniedPage code={accessFailure?.code} />
      </React.Suspense>
    )
  }

  // Signed in but app user or permissions still loading → show loading
  if (!user || permissionsLoading) {
    return <LoadingScreen />
  }

  if (hasRole("safety-admin") && location.pathname.startsWith("/dashboard")) {
    return <Navigate to="/incidents/reports" replace />
  }

  // Signed in + user loaded → main app
  return (
    <React.Fragment>
      <div className="flex flex-row">
        <Sidebar />

        <Routes>
          <Route path="*" element={<Navigate to="/dashboard/summary" replace />} />

          <Route path="/temp/dataset-overlap-viz" Component={DatasetOverlapViz} />
          <Route path="/temp/form-structure-overview" Component={FormStructureOverview} />

          <Route path="/dashboard/summary" element={<Navigate to="/dashboard/lagging" replace />} />
          <Route path="/dashboard/lagging" Component={Lagging} />
          <Route path="/dashboard/leading" Component={Leading} />
          <Route path="/dashboard/configuration" Component={DashboardConfiguration} />

          <Route path="/groups">
            <Route index element={<Navigate to="/groups/create" replace />} />
            <Route path="create" Component={CreateGroup} />

            <Route path=":id" Component={ViewGroup} />
          </Route>

          <Route path="/users" element={<Navigate to="/admin/users" replace />} />

          <Route
            path="/admin/*"
            element={
              <AdminRoute>
                <Routes>
                  <Route
                    path="organizations"
                    element={
                      <AdminPageGuard permission="admin.organizations.view">
                        <OrganizationManagement />
                      </AdminPageGuard>
                    }
                  />
                  <Route
                    path="organizations/hours"
                    element={
                      <AdminPageGuard permission="admin.hours.view">
                        <HoursEntry />
                      </AdminPageGuard>
                    }
                  />
                  <Route
                    path="users"
                    element={
                      <AdminPageGuard permission="admin.users.view">
                        <UserManagement />
                      </AdminPageGuard>
                    }
                  />
                  <Route
                    path="entity-assignments"
                    element={
                      <AdminPageGuard permission="admin.entities.view">
                        <UserEntityAssignments />
                      </AdminPageGuard>
                    }
                  />
                  <Route
                    path="identity-and-sso/*"
                    element={
                      <EnterpriseSsoGuard>
                        <IdentityAndSso />
                      </EnterpriseSsoGuard>
                    }
                  />
                  <Route path="*" element={<Navigate to="/dashboard/summary" replace />} />
                </Routes>
              </AdminRoute>
            }
          />

          <Route path="/incidents">
            <Route index element={<Navigate to="/incidents/reports" replace />} />
            <Route path="reports" Component={Incidents} />
            <Route path="reports/new" element={<CreateReport reportType="incident" />} />
            <Route path="reports/new/:formId" element={<CreateReport reportType="incident" />} />
            <Route path="reports/:id" Component={Incident} />

            <Route
              path="forms"
              element={
                <PermissionGuard permission="incidents.forms.view">
                  <IncidentForms />
                </PermissionGuard>
              }
            />
          </Route>

          <Route path="/inspections">
            <Route index element={<Navigate to="/inspections/reports" replace />} />
            <Route
              path="create-form"
              element={
                <PermissionGuard permission="forms.create">
                  <Navigate to="/inspections/forms/new/edit" replace />
                </PermissionGuard>
              }
            />

            <Route path="reports" Component={InspectionReports} />
            <Route path="reports/new" element={<CreateReport reportType="inspection" />} />
            <Route path="reports/new/:formId" element={<CreateReport reportType="inspection" />} />
            <Route path="reports/:id" Component={InspectionReport} />

            <Route
              path="forms"
              element={
                <PermissionGuard permission="inspections.forms.view">
                  <InspectionForms />
                </PermissionGuard>
              }
            />
            <Route
              path="forms/new/edit"
              element={
                <PermissionGuard permission="forms.create">
                  <InspectionFormBuilder />
                </PermissionGuard>
              }
            />
            <Route
              path="forms/:id/edit"
              element={
                <PermissionGuard permission="forms.update">
                  <InspectionFormBuilder />
                </PermissionGuard>
              }
            />
            <Route
              path="forms/:id"
              element={
                <PermissionGuard permission="forms.update">
                  <InspectionForm />
                </PermissionGuard>
              }
            />
          </Route>

          <Route path="/issues">
            <Route index element={<Navigate to="/issues/reports" replace />} />
            <Route
              path="create-form"
              element={
                <PermissionGuard permission="forms.create">
                  <Navigate to="/issues/forms/new/edit" replace />
                </PermissionGuard>
              }
            />

            <Route
              path="forms"
              element={
                <PermissionGuard permission="issues.forms.view">
                  <Forms />
                </PermissionGuard>
              }
            />
            <Route
              path="forms/new/edit"
              element={
                <PermissionGuard permission="forms.create">
                  <IssueFormBuilder />
                </PermissionGuard>
              }
            />
            <Route
              path="forms/:id/edit"
              element={
                <PermissionGuard permission="forms.update">
                  <IssueFormBuilder />
                </PermissionGuard>
              }
            />
            <Route
              path="forms/:id"
              element={
                <PermissionGuard permission="forms.update">
                  <Form />
                </PermissionGuard>
              }
            />

            <Route path="reports" Component={IssueReports} />
            <Route path="reports/new" element={<CreateReport reportType="issue" />} />
            <Route path="reports/new/:formId" element={<CreateReport reportType="issue" />} />
            <Route path="reports/:id" Component={IssueReport} />
          </Route>

          <Route path="/settings">
            <Route index element={<Navigate to="/settings/profile" replace />} />
            <Route path="profile/*" Component={Profile} />
            <Route path="support" Component={Support} />
            <Route path="faq" Component={FAQ} />
          </Route>
        </Routes>
      </div>
    </React.Fragment>
  )
}

export default Router
